import type {
  IChannelPayloadPlaylistAssignment,
  IScheduleRootAssignment
} from '../models/player-registration-payload'
import type { TGeoLatLng } from '../models/playlist-assignment-geo-filter'
import type { TFrame } from '../models/playlist'
import { Temporal } from '@js-temporal/polyfill'
import { isDateIncludedInTimeBlocks } from '../models/time-block'
import { combineRegistrationScheduleRootsForPlayback } from './combineRegistrationScheduleRootsForPlayback'
import { isPlaylistAssignmentGeoSatisfied } from './playlistAssignmentGeoFilter'
import { isFrameGeoSatisfied } from './frameGeoFilter'
import { operatorAssignedPlayerMasterPlaylistUrn } from './playerMasterPlaylist'

/** Optional geo context for frame activity (time + radius). */
export type TFrameActiveAtOptions = {
  /**
   * Resolved player point. When omitted, frame geo is skipped (editor / duration).
   * When `null`, unknown location — honor {@link TFrame.showWhenLocationUnavailable}.
   */
  geoPoint?: TGeoLatLng | null
  /** When true, ignore frame {@link TFrame.geoFilter} (idle diagnostics / combine spine). */
  skipGeoFilter?: boolean
}

/** Optional geo context for playlist-assignment activity (time + radius). */
export type TPlaylistAssignmentActiveAtOptions = {
  /**
   * Resolved player point (GPS → venue → stamped fallback). Required when the assignment
   * has a geofilter; missing point fails closed.
   */
  geoPoint?: TGeoLatLng | null
  /**
   * When true, ignore {@link IChannelPayloadPlaylistAssignment.geoFilter} (used when combining
   * registration roots so geo-gated rows stay in the spine; {@link resolvePlaybackSegments} applies geo).
   */
  skipGeoFilter?: boolean
}

/** True when the string is date-only (yyyy-MM-dd), so we normalize to full day UTC. */
function isDateOnly(s: string): boolean {
  return s.length === 10 && !s.includes('T')
}

/**
 * Normalize start boundary for comparison: date-only → start of day UTC; full ISO as-is.
 * Exported for UI (e.g. frame schedule date tooltips) so copy matches {@link isFrameActiveAt}.
 */
export function effectiveFrameScheduleStartIso(startDate: string | null): string | null {
  if (startDate == null) {
    return null
  }
  if (isDateOnly(startDate)) {
    return `${startDate}T00:00:00.000Z`
  }
  return startDate
}

/**
 * Normalize end boundary for comparison (end is exclusive).
 * Date-only → start of next day UTC so [day, nextDay) covers the full day; full ISO as-is.
 * Exported for UI (e.g. frame schedule date tooltips) so copy matches {@link isFrameActiveAt}.
 */
export function effectiveFrameScheduleEndIsoExclusive(endDate: string | null): string | null {
  if (endDate == null) {
    return null
  }
  if (isDateOnly(endDate)) {
    const d = Temporal.PlainDate.from(endDate)
    const next = d.add({ days: 1 })
    return `${next.toString()}T00:00:00.000Z`
  }
  return endDate
}

/**
 * Full ISO schedule boundaries use `[start, end)` with exclusive `end`. When a date-range UI sends the
 * same calendar day for both pickers as `YYYY-MM-DDT00:00:00.000Z`, `start === end` yields a zero-width
 * window and nothing plays (e.g. single-day ad orders that persist period start/end as UTC midnight).
 * If both stored values are full ISO (contain `T`) and normalize to the same instant at UTC midnight,
 * treat the range as that whole UTC calendar day: exclusive end becomes next UTC midnight.
 */
function effectiveExclusiveEndIsoCoalescingDegenerateMidnightRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  startIso: string | null,
  endIso: string | null
): string | null {
  if (startIso == null || endIso == null || startIso !== endIso) {
    return endIso
  }
  const startHasTime = startDate != null && startDate.includes('T')
  const endHasTime = endDate != null && endDate.includes('T')
  if (!startHasTime || !endHasTime) {
    return endIso
  }
  const zdt = Temporal.Instant.from(startIso).toZonedDateTimeISO('UTC')
  if (zdt.hour !== 0 || zdt.minute !== 0 || zdt.second !== 0 || zdt.millisecond !== 0) {
    return endIso
  }
  const next = zdt.toPlainDate().add({ days: 1 })
  return `${next.toString()}T00:00:00.000Z`
}

/**
 * Returns whether a campaign is active at the given reference instant (UTC).
 * startDate/endDate are ISO strings (full or date-only). When date-only (yyyy-MM-dd),
 * they are interpreted as full day in UTC (start 00:00:00.000Z, end 23:59:59.999Z).
 * When startDate or endDate is null, that boundary is ignored.
 * End is exclusive: active when start <= ref < end. Start is inclusive; format-agnostic.
 *
 * @param c - Campaign assignment with startDate/endDate
 * @param referenceInstant - Instant to check (UTC)
 */
export function isCampaignActiveAt(
  c: IScheduleRootAssignment,
  referenceInstant: Temporal.Instant
): boolean {
  const refEpoch = referenceInstant.epochMilliseconds
  const startIso = effectiveFrameScheduleStartIso(c.startDate)
  const endIso = effectiveExclusiveEndIsoCoalescingDegenerateMidnightRange(
    c.startDate,
    c.endDate,
    startIso,
    effectiveFrameScheduleEndIsoExclusive(c.endDate)
  )
  const afterStart = startIso == null || refEpoch >= Temporal.Instant.from(startIso).epochMilliseconds
  const beforeEnd = endIso == null || refEpoch < Temporal.Instant.from(endIso).epochMilliseconds
  return afterStart && beforeEnd
}

/**
 * Returns whether a playlist assignment is active at the given reference instant.
 * Honors startDate/endDate (same semantics as isCampaignActiveAt) and timeBlocks:
 * when timeBlocks is non-empty, the reference time must fall within at least one block.
 * When {@link IChannelPayloadPlaylistAssignment.geoFilter} is set, also requires
 * {@link TPlaylistAssignmentActiveAtOptions.geoPoint} inside the radius (fail closed if missing).
 * On the player, `referenceInstant` is device local wall clock encoded as UTC (`getLocalAsUtcInstant`);
 * day-of-week and time-of-day in blocks then match that spine. When timeBlocks is empty, only the date range applies.
 *
 * @param assignment - Playlist assignment with startDate, endDate, timeBlocks, optional geoFilter
 * @param referenceInstant - Instant to check (player: local-as-UTC spine; server UTC checks: convert first)
 */
export function isPlaylistAssignmentActiveAt(
  assignment: IChannelPayloadPlaylistAssignment,
  referenceInstant: Temporal.Instant,
  options?: TPlaylistAssignmentActiveAtOptions
): boolean {
  const refEpoch = referenceInstant.epochMilliseconds
  const startIso = effectiveFrameScheduleStartIso(assignment.startDate)
  const endIso = effectiveExclusiveEndIsoCoalescingDegenerateMidnightRange(
    assignment.startDate,
    assignment.endDate,
    startIso,
    effectiveFrameScheduleEndIsoExclusive(assignment.endDate)
  )
  const afterStart = startIso == null || refEpoch >= Temporal.Instant.from(startIso).epochMilliseconds
  const beforeEnd = endIso == null || refEpoch < Temporal.Instant.from(endIso).epochMilliseconds
  if (!afterStart || !beforeEnd) {
    return false
  }
  if ((assignment.timeBlocks?.length ?? 0) > 0) {
    if (!isDateIncludedInTimeBlocks(referenceInstant, assignment.timeBlocks)) {
      return false
    }
  }
  if (options?.skipGeoFilter === true) {
    return true
  }
  return isPlaylistAssignmentGeoSatisfied(assignment.geoFilter ?? null, options?.geoPoint ?? null)
}

/**
 * Playlist row active for playback at `referenceInstant`, with an optional bypass for the
 * operator-assigned per-screen master (still plays outside its `timeBlocks` when set).
 *
 * Bypass is scoped to the **master schedule-root slot** (or an explicit
 * {@link IChannelPayloadPlaylistAssignment.operatorMasterTimeBlockBypass} stamp from combine),
 * not every row whose `playlistUrn` happens to equal the master URN under another root.
 * Geofilter still applies when present (bypass only skips time/date for the master slot).
 */
export function isPlaylistAssignmentActiveForPlayback(
  assignment: IChannelPayloadPlaylistAssignment,
  referenceInstant: Temporal.Instant,
  operatorMasterPlaylistUrn?: string | null,
  options?: { scheduleRootUrn?: string | null } & TPlaylistAssignmentActiveAtOptions
): boolean {
  const geoOpts: TPlaylistAssignmentActiveAtOptions = {
    geoPoint: options?.geoPoint,
    skipGeoFilter: options?.skipGeoFilter
  }
  if (assignment.operatorMasterTimeBlockBypass === true) {
    if (options?.skipGeoFilter === true) {
      return true
    }
    return isPlaylistAssignmentGeoSatisfied(assignment.geoFilter ?? null, options?.geoPoint ?? null)
  }
  if (assignment.operatorMasterTimeBlockBypass === false) {
    return isPlaylistAssignmentActiveAt(assignment, referenceInstant, geoOpts)
  }
  const masterUrn = operatorAssignedPlayerMasterPlaylistUrn(operatorMasterPlaylistUrn)
  const masterLower = (masterUrn?.trim() ?? '').toLowerCase()
  const rootLower = (options?.scheduleRootUrn?.trim() ?? '').toLowerCase()
  if (
    masterLower !== ''
    && rootLower !== ''
    && rootLower === masterLower
    && assignment.playlistUrn.trim().toLowerCase() === masterLower
  ) {
    if (options?.skipGeoFilter === true) {
      return true
    }
    return isPlaylistAssignmentGeoSatisfied(assignment.geoFilter ?? null, options?.geoPoint ?? null)
  }
  return isPlaylistAssignmentActiveAt(assignment, referenceInstant, geoOpts)
}

/**
 * Selects the first campaign that is active at the reference instant (UTC).
 * Returns null if no campaign is active (no fallback).
 *
 * @param campaigns - Channel campaign assignments (ordered by priority)
 * @param referenceInstant - Instant to check (UTC); defaults to now UTC
 * @returns First active campaign, or null if none match
 */
export function selectFirstCampaignActiveForDate(
  campaigns: IScheduleRootAssignment[],
  referenceInstant: Temporal.Instant = Temporal.Now.instant()
): IScheduleRootAssignment | null {
  if (campaigns.length === 0) {
    return null
  }
  return campaigns.find(c => isCampaignActiveAt(c, referenceInstant)) ?? null
}

/**
 * Multiplexes all schedule roots active at `referenceInstant` for player registration playback
 * (master first when `masterPlaylistUrn` is passed via {@link combineRegistrationScheduleRootsForPlayback} options).
 * Returns `null` when no root is active (name is historical; there is no inactive-root fallback).
 *
 * @see combineRegistrationScheduleRootsForPlayback
 */
export function selectActiveScheduleRootOrFirst(
  campaigns: IScheduleRootAssignment[],
  referenceInstant: Temporal.Instant = Temporal.Now.instant(),
  options?: { masterPlaylistUrn?: string | null }
): IScheduleRootAssignment | null {
  return combineRegistrationScheduleRootsForPlayback(campaigns, referenceInstant, options)
}

/**
 * Returns whether a frame should be included in playback at the given reference instant.
 * Frames with no scheduling fields (startDate, endDate, timeBlocks) are always active for time.
 * When {@link TFrame.geoFilter} is set, geo is evaluated when `options.geoPoint` is provided
 * (`undefined` skips geo; `null` = unknown location).
 *
 * @param frame - The frame to test (from TFrame)
 * @param referenceInstant - Instant to check (UTC)
 * @param options - Optional geo context for playback
 */
export function isFrameActiveAt(
  frame: TFrame,
  referenceInstant: Temporal.Instant,
  options?: TFrameActiveAtOptions
): boolean {
  const hasStart = frame.startDate != null
  const hasEnd = frame.endDate != null
  const hasBlocks = (frame.timeBlocks?.length ?? 0) > 0
  const hasTimeGate = hasStart || hasEnd || hasBlocks
  if (hasTimeGate) {
    const refEpoch = referenceInstant.epochMilliseconds
    const startIso = effectiveFrameScheduleStartIso(frame.startDate ?? null)
    const endIso = effectiveExclusiveEndIsoCoalescingDegenerateMidnightRange(
      frame.startDate ?? null,
      frame.endDate ?? null,
      startIso,
      effectiveFrameScheduleEndIsoExclusive(frame.endDate ?? null)
    )
    const afterStart = startIso == null || refEpoch >= Temporal.Instant.from(startIso).epochMilliseconds
    const beforeEnd = endIso == null || refEpoch < Temporal.Instant.from(endIso).epochMilliseconds
    if (!afterStart || !beforeEnd) {
      return false
    }
    if (hasBlocks && !isDateIncludedInTimeBlocks(referenceInstant, frame.timeBlocks!)) {
      return false
    }
  }
  return isFrameGeoSatisfied(frame, options?.geoPoint, {
    skipGeoFilter: options?.skipGeoFilter
  })
}
